import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Client } from '../../models/client';
import { PdfService } from '../../services/pdf.service';
import { ImmobilierService } from '../../services/immobilier.service';
import { Contract, Payment } from '../../models/contract';
import * as XLSX from 'xlsx';

import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-client-detail-dialog',
    templateUrl: './client-detail-dialog.component.html',
    styleUrls: ['./client-detail-dialog.component.css']
})
export class ClientDetailDialogComponent implements OnInit {

    client: Client;
    contracts: Contract[] = [];
    payments: Payment[] = [];

    constructor(
        public dialogRef: MatDialogRef<ClientDetailDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private pdfService: PdfService,
        private immobilierService: ImmobilierService,
        private snackBar: MatSnackBar
    ) {
        this.client = data.client;
    }

    ngOnInit(): void {
        this.loadContracts();
    }

    loadContracts(): void {
        this.immobilierService.getContracts().subscribe({
            next: (allContracts) => {
                this.contracts = allContracts.filter(c =>
                    c.ownerId === this.client.id || c.tenantBuyerId === this.client.id
                );
                this.loadPaymentsForContracts();
            },
            error: (error) => {
                console.error('Erreur lors du chargement des contrats', error);
            }
        });
    }

    loadPaymentsForContracts(): void {
        const contractIds = this.contracts.map(c => c.id);
        if (contractIds.length === 0) return;

        this.immobilierService.getAllPayments().subscribe({
            next: (allPayments) => {
                this.payments = allPayments.filter(p => contractIds.includes(p.contractId));
            },
            error: (error) => {
                console.error('Erreur lors du chargement des paiements', error);
            }
        });
    }

    downloadContract(contract: Contract): void {
        if (contract.contractDocumentUrl) {
            this.immobilierService.downloadContractFile(contract.contractDocumentUrl).subscribe({
                next: (blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = contract.contractDocumentUrl!;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                },
                error: (error) => {
                    console.error('Download failed', error);
                    this.snackBar.open('Erreur lors du téléchargement du contrat', 'Fermer', { duration: 3000 });
                }
            });
        } else {
            this.snackBar.open('Aucun document associé à ce contrat', 'Fermer', { duration: 3000 });
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    convert(): void {
        this.dialogRef.close({ action: 'convert', client: this.client });
    }

    edit(): void {
        this.dialogRef.close({ action: 'edit', client: this.client });
    }

    exportPdf(): void {
        const fileName = `Fiche_Client_${this.client.firstName}_${this.client.lastName}`;
        this.pdfService.generatePdf('client-pdf-content', fileName);
    }

    exportPaymentsToExcel(): void {
        if (!this.payments || this.payments.length === 0) return;

        const extract = this.payments.map(p => {
            const contract = this.contracts.find(c => c.id === p.contractId);
            const propName = p.propertyName || (contract ? contract.propertyName : '') || '';
            const propRef = contract ? contract.propertyReference : '';
            const bien = (propName && propRef) ? `${propName} (${propRef})` : (propName || propRef || 'N/A');

            return {
                'Date': new Date(p.paymentDate).toLocaleDateString('fr-FR'),
                'Bien': bien,
                'Contrat': contract ? contract.contractNumber : '',
                'Montant': p.amount,
                'Type': p.type,
                'Moyen': p.paymentMethod,
                'Statut': p.status,
                'Référence Paiement': p.reference || '',
                'Notes': p.notes || ''
            };
        });

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(extract);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Paiements');

        const fileName = `Historique_Paiements_${this.client.firstName}_${this.client.lastName}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }
}
